import { OrderBoxHeaderIcon } from "./OrderBoxHeaderIcon";
import { OrderBoxHeaderTitle } from "./OrderBoxHeaderTitle";
import styled, { useTheme } from "styled-components";
import { computeBadgesByOrderType } from "./computeBadgesByOrderType";

import { ChevronIcon } from "src/apps/components/chevronIcon/ChevronIcon";
import { ICON_SIZES } from "src/apps/components/icon/Icon";
import { useIsMedia } from "src/apps/hooks/useIsMedia";
import { OrderPayslip } from "src/contexts/documentationArea/domain/OrderPayslip";
import { OrderModel } from "src/contexts/documentationArea/domain/OrderModel";
import { OrderOtherDocument } from "src/contexts/documentationArea/domain/OrderOtherDocument";
import { ExtractionState } from "src/contexts/documentationArea/domain/ExtractionState";
import { Button } from "src/apps/components/button/Button";
import { useTranslations } from "src/apps/hooks/useTranslations";
import { LuArrowDownToLine, LuArrowUpFromLine } from "react-icons/lu";
import { T } from "src/apps/components/i18n/Translation";
import { MediumText, TextBold, TextContent } from "src/apps/components/typography/Texts";
import { MEDIA_MOBILE_BREAKPOINT } from "src/apps/config/constants";
import { colors } from "@permisso/permisso-components";
import { getValueFromExtractionDetails } from "src/contexts/documentationArea/application/ExtractionDetailsToAccentKey";
import { TEST_IDS } from "src/apps/config/testIds";

interface OrderBoxHeaderProps {
  order: OrderModel | OrderPayslip | OrderOtherDocument;
  onToggleClick: () => void;
  onUploadClick: () => void;
  isExpanded: boolean;
}

const ExtractionMessage = ({ order }: { order: OrderModel }) => {
  const { t } = useTranslations();

  const state = order.getExtractionState();
  const color = getExtractionMessageColor(state);
  const extractionDetails = order.getExtractionDetails();
  if (!color || !extractionDetails) return null;

  return (
    <ExtractionMessageText $color={color}>
      {t(getValueFromExtractionDetails(extractionDetails.reason))}
    </ExtractionMessageText>
  );
};

const getExtractionMessageColor = (state: ExtractionState): string | null => {
  switch (state) {
    case ExtractionState.FAILED:
      return colors.defaultRed;
    case ExtractionState.PENDING:
      return colors.darkYellow;
    default:
      return null;
  }
};

export const OrderBoxHeader = ({
  order,
  onToggleClick,
  onUploadClick,
  isExpanded,
}: OrderBoxHeaderProps) => {
  const { isMobile } = useIsMedia();
  const { t } = useTranslations();
  const { primaryColor } = useTheme();
  const shouldBeToggleable = order.getResponses().length > 0 && isMobile;
  const badges = computeBadgesByOrderType(order);
  const orderHeaderTestId = order.isModel()
    ? TEST_IDS.orders.header(order.getRequest().type)
    : TEST_IDS.orders.header(order.getType());

  return (
    <HeaderContainer
      data-testid={orderHeaderTestId}
      onClick={shouldBeToggleable ? onToggleClick : undefined}
      $touchable={shouldBeToggleable}
    >
      <Left>
        <FirstRow>
          <FirstRowPart>
            <OrderBoxHeaderIcon order={order} />
            <TitleContainer>
              <TextBold>
                <OrderBoxHeaderTitle order={order} />
              </TextBold>
              {order.isOtherDocument() && order.getRequest().description ? (
                <Description>{order.getRequest().description}</Description>
              ) : null}
            </TitleContainer>
          </FirstRowPart>
          {shouldBeToggleable ? (
            <ToggleButton>
              <ChevronIcon rotated={isExpanded} size={ICON_SIZES.s24} />
            </ToggleButton>
          ) : null}
        </FirstRow>
        {badges && badges?.length > 0 && isExpanded ? <SecondRow>{badges}</SecondRow> : null}
        {order.isModel() ? <ExtractionMessage order={order} /> : null}
      </Left>
      {order.canManuallyUpload() && !isMobile ? (
        <Right onClick={(event) => event.stopPropagation()}>
          <Button
            dataTestId={TEST_IDS.orders.uploadButton}
            variant="outline"
            text={t("common.upload_file")}
            onClick={onUploadClick}
            disabled={order.isProcessing()}
            leftIcon={<LuArrowUpFromLine size={20} />}
          />
        </Right>
      ) : order.canOnlyAutomaticallyExtract() && !order.hasValidResponses() ? (
        <Row>
          <LuArrowDownToLine color={primaryColor} />
          <MediumText $color={colors.grey700}>
            <T k="documentation_area.automatic_extraction.label" />
          </MediumText>
        </Row>
      ) : null}
    </HeaderContainer>
  );
};

const FirstRowPart = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  gap: 0.5rem;
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.5rem;
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  justify-content: center;
  align-self: flex-start;
`;

const HeaderContainer = styled.div<{ $touchable: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  font-weight: bold;
  gap: 0.375rem;
  cursor: ${({ $touchable }) => ($touchable ? "pointer" : "default")};
  @media (max-width: ${MEDIA_MOBILE_BREAKPOINT}rem) {
    gap: 1rem;
    flex-direction: column;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.5rem;
`;

const Description = styled(TextContent)`
  color: ${colors.grey700};
`;

const FirstRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const SecondRow = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 0.25rem;
`;

const ToggleButton = styled.button``;

const ExtractionMessageText = styled(TextContent)`
  margin-top: 0.5rem;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.25rem;
  align-items: center;
  align-self: flex-start;
`;
